import React, { useEffect } from 'react'
import { PersistGate } from 'redux-persist/integration/react'
import { ResetCSS } from '@mprolab/uikit'
import { ToastListener } from 'contexts/ToastsContext'
import useEagerConnect from 'hooks/useEagerConnect'
import { useMe } from 'hooks/useMe'
import { persistor, useStore } from 'state'
import { useGlobalSettings } from 'state/global/hooks'
import AppLayout from 'layouts/AppLayout'
import Providers from './Providers'
import AppRoutes from './AppRoutes'
import SupportWidget from './components/SupportWidget/SupportWidget'
import DefaultMeta from './components/DefaultMeta'
import ErrorBoundary from './components/ErrorBoundary'
import { installGlobalErrorReporting } from './utils/reportClientError'

installGlobalErrorReporting()

/** Rendered while redux-persist rehydrates. Deliberately dependency-free: it has
 *  to work even when whatever follows it does not. */
function RehydrationShell() {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        opacity: 0.5,
        fontFamily: 'sans-serif',
      }}
    >
      Loading MPRO Lab…
    </div>
  )
}

function GlobalHooks() {
  useEagerConnect()
  useMe()
  useGlobalSettings()
  return null
}

export default function Root() {
  const store = useStore(undefined)

  useEffect(() => {
    const gtag = import.meta.env.VITE_GOOGLE_GTAG
    if (!gtag) return undefined
    const s = document.createElement('script')
    s.async = true
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gtag}`
    document.head.appendChild(s)
    const inline = document.createElement('script')
    inline.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${gtag}');
    `
    document.head.appendChild(inline)
    return () => {
      s.remove()
      inline.remove()
    }
  }, [])

  useEffect(() => {
    if (import.meta.env.DEV && import.meta.env.VITE_SLISE_IN_DEV !== '1') {
      return undefined
    }
    const w = window as Window & { __mprolabSliseBootstrap?: boolean }
    if (w.__mprolabSliseBootstrap) return undefined
    w.__mprolabSliseBootstrap = true
    const pix = document.createElement('script')
    pix.id = 'slise-pix3l-bootstrap'
    pix.innerHTML = `!function(p,i,x,e,l,s){p.slq||(s=p.slq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments)},s.queue=[],l=i.createElement(x),l.async=!0,l.src='https://v1.slise.xyz/scripts/pix3l.js',l.id='slise-pix3l',l.setAttribute('data-slise-adv-id','adv-210'),e=i.getElementsByTagName(x)[0],e.parentNode.insertBefore(l,e))}(window,document,'script');`
    document.body.appendChild(pix)
    return undefined
  }, [])

  return (
    <>
      <DefaultMeta />
      {/* Outermost boundary: a failure inside the providers — a wallet extension
          that does not answer, a store that will not build — used to unmount the
          whole application and leave an empty page. */}
      <ErrorBoundary>
        <Providers store={store}>
          <ResetCSS />
          <GlobalHooks />
          {/* loading={null} rendered literally nothing while rehydration was
              pending, so a stalled rehydration was indistinguishable from a
              crash. A shell at least proves the app is alive. */}
          <PersistGate loading={<RehydrationShell />} persistor={persistor}>
            <AppLayout>
              {/* Inner boundary: one broken route must not take the layout with it. */}
              <ErrorBoundary>
                <AppRoutes />
              </ErrorBoundary>
              <ToastListener />
              {/* Reachable from every page, and only for a connected wallet. */}
              <SupportWidget />
            </AppLayout>
          </PersistGate>
        </Providers>
      </ErrorBoundary>
    </>
  )
}
